import { useState, useEffect, useCallback, useRef } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

// Endesa identifica cada instalación por su dirección; los demás clientes traen
// la calle real en "dirección" pero el nombre que identifica la instalación (el
// cliente final) viene en la columna "usuario" del Excel (usuario_responsable).
function instNombre(inst) {
  const esEndesa = (inst?.cliente || '').toUpperCase().includes('ENDESA');
  if (esEndesa) return inst?.direccion || '';
  return inst?.usuario_responsable || inst?.direccion || '';
}

function getISOWeekYear(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { week, year: d.getFullYear() };
}

function getWeekDates(year, week) {
  const jan4 = new Date(year, 0, 4);
  const dow  = jan4.getDay() || 7;
  const mon  = new Date(jan4);
  mon.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  return [0, 1, 2, 3, 4].map(i => {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i);
    return d;
  });
}

function toArr(val) {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

export default function Planificacion() {
  const user    = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);

  const now = getISOWeekYear();
  const [week, setWeek]                   = useState(now.week);
  const [year, setYear]                   = useState(now.year);
  const [employees, setEmployees]         = useState([]);
  const [installations, setInstallations] = useState([]);
  const [grid, setGrid]                   = useState({});
  const [sede, setSede]                   = useState('sevilla');
  const [marcas, setMarcas]               = useState({ festivos: [], vacaciones: {}, bajas: {} });
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [dayDetail, setDayDetail]         = useState(null);
  const [publishing, setPublishing]       = useState(false);
  const [dirty, setDirty]                 = useState(false);
  const [hasDraft, setHasDraft]           = useState(false);
  const [publishedAt, setPublishedAt]     = useState(null);
  const [showPairs, setShowPairs]         = useState(false);
  const [dragOver, setDragOver]           = useState(null); // {empId, dayIdx}
  const lastUpdatedAt                     = useRef(null);
  const gridRef                           = useRef(grid);
  const dragRef                           = useRef(null); // {empId, dayIdx}
  useEffect(() => { gridRef.current = grid; }, [grid]);

  useEffect(() => {
    api.get('/api/planificacion/empleados')
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
    api.get('/api/installations/lista')
      .then(r => setInstallations(r.data || []))
      .catch(() => {});
  }, []);

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, marcasRes] = await Promise.all([
        api.get(`/api/planificacion/data?semana=${week}&año=${year}`),
        api.get(`/api/planificacion/marcas?semana=${week}&año=${year}`),
      ]);
      const row = planRes.data;
      setGrid(row?.working_data || row?.data || {});
      setHasDraft(row?.has_draft || false);
      setPublishedAt(row?.published_at || null);
      lastUpdatedAt.current = row?.updated_at || null;
      setMarcas({ festivos: marcasRes.festivos || [], vacaciones: marcasRes.vacaciones || {}, bajas: marcasRes.bajas || {} });
    } catch { setGrid({}); }
    finally { setLoading(false); setDirty(false); }
  }, [week, year]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  useEffect(() => {
    const id = setInterval(async () => {
      if (dirty || document.hidden) return;
      try {
        const r = await api.get(`/api/planificacion/data?semana=${week}&año=${year}`);
        const row = r.data;
        if (row?.updated_at && row.updated_at !== lastUpdatedAt.current) {
          setGrid(row?.working_data || row?.data || {});
          setHasDraft(row?.has_draft || false);
          lastUpdatedAt.current = row.updated_at;
        }
      } catch {}
    }, 15000);
    return () => clearInterval(id);
  }, [week, year, dirty]);

  // ── Grupos (2 o más personas cuyas celdas se sincronizan) ───────────────────
  // Viven dentro de los datos de la semana (como las celdas), no son globales:
  // un grupo definido en una semana no afecta a las demás.
  const groups = grid['__groups'] || [];

  function saveGroups(newGroups) {
    setGrid(prev => ({ ...prev, '__groups': newGroups }));
    setDirty(true);
  }

  function getGroupMates(empId) {
    const sId = String(empId);
    const g = groups.find(g => g.members.includes(sId));
    return g ? g.members.filter(m => m !== sId) : [];
  }

  function addGroup(memberIds) {
    const members = [...new Set(memberIds.map(String))];
    if (members.length < 2) return;
    saveGroups([...groups, { members }]);
  }

  function removeGroup(idx) {
    saveGroups(groups.filter((_, i) => i !== idx));
  }

  // ── Grid mutations ─────────────────────────────────────────────────────────
  function setItemDesc(empId, dayIdx, val, desc, _mirror = true) {
    setGrid(prev => ({
      ...prev,
      '__descs': {
        ...(prev['__descs'] || {}),
        [String(empId)]: {
          ...(prev['__descs']?.[String(empId)] || {}),
          [String(dayIdx)]: {
            ...(prev['__descs']?.[String(empId)]?.[String(dayIdx)] || {}),
            [val]: desc,
          },
        },
      },
    }));
    setDirty(true);
    if (_mirror) {
      getGroupMates(empId).forEach(mid => setItemDesc(mid, dayIdx, val, desc, false));
    }
  }

  function getCell(empId, day) {
    return toArr(grid[String(empId)]?.[String(day)]);
  }

  function addToCell(empId, day, val, _mirror = true) {
    const v = val.trim();
    if (!v) return;
    setGrid(prev => {
      const arr = toArr(prev[String(empId)]?.[String(day)]);
      if (arr.includes(v)) return prev;
      return { ...prev, [String(empId)]: { ...(prev[String(empId)] || {}), [String(day)]: [...arr, v] } };
    });
    setDirty(true);
    if (_mirror) {
      getGroupMates(empId).forEach(mid => addToCell(mid, day, val, false));
    }
  }

  function removeFromCell(empId, day, idx) {
    setGrid(prev => {
      const a = toArr(prev[String(empId)]?.[String(day)]);
      return { ...prev, [String(empId)]: { ...(prev[String(empId)] || {}), [String(day)]: a.filter((_, i) => i !== idx) } };
    });
    setDirty(true);
  }

  async function savePlan() {
    document.activeElement?.blur();
    await new Promise(r => setTimeout(r, 80));
    setSaving(true);
    try {
      const r = await api.put('/api/planificacion/data', { semana: week, año: year, data: gridRef.current });
      lastUpdatedAt.current = r.data?.updated_at || null;
      setHasDraft(r.data?.has_draft ?? true);
      setDirty(false);
    } catch (e) { alert('Error al guardar: ' + e.message); }
    finally { setSaving(false); }
  }

  async function publishPlan() {
    if (!window.confirm('¿Publicar la planificación? Los técnicos verán los cambios inmediatamente.')) return;
    setPublishing(true);
    try {
      const r = await api.post('/api/planificacion/publish', { semana: week, año: year });
      lastUpdatedAt.current = r.data?.updated_at || null;
      setHasDraft(false);
      setPublishedAt(r.data?.published_at || null);
      setDirty(false);
    } catch (e) { alert('Error al publicar: ' + e.message); }
    finally { setPublishing(false); }
  }

  function clearWeek() {
    if (!window.confirm(`¿Borrar toda la planificación de la semana ${week} / ${year}?\n\nEsta acción vaciará todas las celdas. Necesitarás guardar y publicar para que sea efectiva.`)) return;
    setGrid({});
    setDirty(true);
  }

  function prevWeek() {
    if (week === 1) { setWeek(52); setYear(y => y - 1); }
    else setWeek(w => w - 1);
  }
  function nextWeek() {
    if (week === 52) { setWeek(1); setYear(y => y + 1); }
    else setWeek(w => w + 1);
  }
  function goToday() { const n = getISOWeekYear(); setWeek(n.week); setYear(n.year); }

  // ── Drag handlers ──────────────────────────────────────────────────────────
  function handleDragStart(empId, dayIdx) {
    dragRef.current = { empId, dayIdx };
  }
  function handleDragOver(e, empId, dayIdx) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragOver || dragOver.empId !== empId || dragOver.dayIdx !== dayIdx) {
      setDragOver({ empId, dayIdx });
    }
  }
  function handleDrop(e, empId, dayIdx) {
    e.preventDefault();
    setDragOver(null);
    const src = dragRef.current;
    dragRef.current = null;
    if (!src || (String(src.empId) === String(empId) && src.dayIdx === dayIdx)) return;
    const srcVals = toArr(gridRef.current[String(src.empId)]?.[String(src.dayIdx)]);
    if (srcVals.length === 0) return;
    srcVals.forEach(v => {
      addToCell(empId, dayIdx, v);
      const srcDesc = gridRef.current['__descs']?.[String(src.empId)]?.[String(src.dayIdx)]?.[v];
      if (srcDesc) setItemDesc(empId, dayIdx, v, srcDesc);
    });
  }
  function handleDragEnd() {
    dragRef.current = null;
    setDragOver(null);
  }

  const weekDates      = getWeekDates(year, week);
  const today          = new Date();
  const myEmployee     = employees.find(e => String(e.user_id) === String(user?.id));
  const sedeEmployees  = employees.filter(e => (e.sede || 'sevilla') === sede);

  function dateStr(dayIdx) {
    const d = weekDates[dayIdx];
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }

  function getMarca(empId, dayIdx) {
    const ds = dateStr(dayIdx);
    if (marcas.festivos.includes(ds)) return 'FESTIVO';
    if (marcas.bajas[String(empId)]?.includes(ds)) return 'BAJA';
    if (marcas.vacaciones[String(empId)]?.includes(ds)) return 'VACACIONES';
    return null;
  }

  const fmtPublished = publishedAt
    ? new Date(publishedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Planificación</div>
          {isAdmin && hasDraft && !dirty && (
            <span style={{
              fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 5,
              background: 'rgba(251,146,60,.15)', color: '#fb923c',
              border: '1px solid rgba(251,146,60,.35)', letterSpacing: '.04em',
            }}>
              BORRADOR SIN PUBLICAR
            </span>
          )}
          {isAdmin && !hasDraft && fmtPublished && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
              background: 'rgba(74,222,128,.1)', color: '#4ade80',
              border: '1px solid rgba(74,222,128,.3)',
            }}>
              Publicado {fmtPublished}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <button onClick={clearWeek}
              style={{ background: 'rgba(239,68,68,.1)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)',
                padding: '7px 14px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 12 }}>
              🗑 Limpiar
            </button>
          )}
          {isAdmin && dirty && (
            <button onClick={savePlan} disabled={saving}
              style={{ background: 'rgba(59,130,246,.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.4)',
                padding: '7px 16px', borderRadius: 7, cursor: 'pointer',
                fontWeight: 800, fontSize: 12, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Guardando…' : '💾 Guardar borrador'}
            </button>
          )}
          {isAdmin && !dirty && (hasDraft || Object.keys(grid).length > 0) && (
            <button onClick={publishPlan} disabled={publishing}
              style={{ background: 'rgba(74,222,128,.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,.4)',
                padding: '7px 16px', borderRadius: 7, cursor: 'pointer',
                fontWeight: 800, fontSize: 12, opacity: publishing ? 0.6 : 1 }}>
              {publishing ? 'Publicando…' : '📢 Publicar'}
            </button>
          )}
          <button onClick={goToday} style={{ ...BTN_NAV, fontSize: 11, padding: '5px 10px' }}>Hoy</button>
          <button onClick={prevWeek} style={BTN_NAV}>‹</button>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)',
            minWidth: 110, textAlign: 'center' }}>
            Sem {week} · {year}
          </div>
          <button onClick={nextWeek} style={BTN_NAV}>›</button>
        </div>
      </div>

      {/* ── Mi semana personal (solo técnicos) ── */}
      {!isAdmin && myEmployee && !loading && (
        <div style={{ marginBottom: 18, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>
            Mi semana — {myEmployee.nombre}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4].map(dayIdx => {
              const date    = weekDates[dayIdx];
              const isToday = date.toDateString() === today.toDateString();
              const myVals  = getCell(myEmployee.id, dayIdx);
              const marca   = getMarca(myEmployee.id, dayIdx);
              const companions = myVals.length > 0
                ? employees.filter(e => {
                    if (e.id === myEmployee.id) return false;
                    return toArr(grid[String(e.id)]?.[String(dayIdx)]).some(v => myVals.includes(v));
                  })
                : [];
              return (
                <div key={dayIdx}
                  onClick={() => setDayDetail(dayIdx)}
                  style={{
                  flex: '1 1 120px', borderRadius: 8, padding: '10px 12px',
                  background: isToday ? 'rgba(245,158,11,.08)' : 'var(--surface2)',
                  border: isToday ? '1.5px solid var(--amber)' : '1px solid var(--border)',
                  cursor: 'pointer',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', marginBottom: 6,
                    color: isToday ? 'var(--amber)' : 'var(--text-dim)' }}>
                    {DIAS[dayIdx]} {date.getDate()}/{date.getMonth() + 1}
                  </div>
                  {marca && <MarcaBadge marca={marca} style={{ marginBottom: 6 }} />}
                  {myVals.length > 0 ? myVals.map((v, i) => (
                    <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4,
                      marginBottom: 2 }}>{v}</div>
                  )) : !marca ? (
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dim)' }}>—</div>
                  ) : null}
                  {companions.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: '#60a5fa', textTransform: 'uppercase', marginBottom: 4 }}>
                        Compañeros
                      </div>
                      {companions.map(c => (
                        <div key={c.id} style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#60a5fa' }}>·</span> {c.nombre}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs sede */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['sevilla','granada'].map(s => (
          <button key={s} onClick={() => setSede(s)} style={{
            padding: '6px 18px', borderRadius: 7, fontWeight: 800, fontSize: 12, cursor: 'pointer',
            textTransform: 'capitalize', letterSpacing: '.04em',
            background: sede === s ? 'var(--amber)' : 'var(--surface2)',
            color: sede === s ? '#000' : 'var(--text-dim)',
            border: sede === s ? 'none' : '1px solid var(--border)',
          }}>{s.charAt(0).toUpperCase()+s.slice(1)}</button>
        ))}
      </div>

      {/* ── Panel de grupos (solo admin) ── */}
      {isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setShowPairs(v => !v)}
            style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: showPairs ? 'var(--amber)' : 'var(--text-dim)',
              padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            🔗 Grupos
            {groups.length > 0 && (
              <span style={{ background: 'rgba(245,158,11,.2)', color: 'var(--amber)', borderRadius: 10,
                padding: '1px 6px', fontSize: 10, fontWeight: 900 }}>{groups.length}</span>
            )}
            <span style={{ fontSize: 9, opacity: .6 }}>{showPairs ? '▲' : '▼'}</span>
          </button>

          {showPairs && (
            <div style={{ marginTop: 6, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '.08em', marginBottom: 10 }}>
                Grupos de esta semana (2 o más) — al editar uno se copia al resto, con instalación y comentario. Recuerda guardar/publicar.
              </div>
              {groups.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 10 }}>Sin grupos definidos</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {groups.map((g, i) => (
                    <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
                      borderRadius: 6, padding: '4px 10px' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                        {g.members.map(m => employees.find(e => String(e.id) === m)?.nombre || m).join(' ⟺ ')}
                      </span>
                      <button onClick={() => removeGroup(i)}
                        style={{ background: 'none', border: 'none', color: '#f87171',
                          cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <AddGroupForm employees={employees} onAdd={addGroup} />
            </div>
          )}
        </div>
      )}

      <datalist id="inst-list">
        {installations.map(inst => (
          <option key={inst.direccion} value={instNombre(inst)} />
        ))}
      </datalist>

      {loading ? (
        <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Cargando…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
            <thead>
              <tr>
                <th style={TH_EMP}>Empleado</th>
                {DIAS.map((d, i) => {
                  const date    = weekDates[i];
                  const isToday = date.toDateString() === today.toDateString();
                  return (
                    <th key={i} style={{
                      ...TH,
                      color: isToday ? 'var(--amber)' : 'var(--text)',
                      borderBottom: isToday ? '2px solid var(--amber)' : '1px solid var(--border)',
                    }}>
                      <div>{d}</div>
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-dim)', marginTop: 1 }}>
                        {date.getDate()}/{date.getMonth() + 1}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sedeEmployees.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 28,
                    color: 'var(--text-dim)', fontSize: 13, border: '1px solid var(--border)' }}>
                    Sin empleados en {sede.charAt(0).toUpperCase()+sede.slice(1)}
                  </td>
                </tr>
              ) : sedeEmployees.map((emp, rowIdx) => {
                const isPartnerRow = groups.some(g => g.members.includes(String(emp.id)));
                return (
                  <tr key={emp.id} style={{ background: rowIdx % 2 === 0 ? 'var(--surface-deep)' : 'var(--surface)' }}>
                    <td style={{ ...TD_EMP }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {emp.nombre}
                        {isAdmin && isPartnerRow && (
                          <span title="Tiene grupo asignado" style={{ fontSize: 12, opacity: .7 }}>🔗</span>
                        )}
                      </div>
                    </td>
                    {[0, 1, 2, 3, 4].map(dayIdx => {
                      const marca   = getMarca(emp.id, dayIdx);
                      const isDropTarget = dragOver && String(dragOver.empId) === String(emp.id) && dragOver.dayIdx === dayIdx;
                      const isSrcCell   = dragRef.current && String(dragRef.current.empId) === String(emp.id) && dragRef.current.dayIdx === dayIdx;
                      return (
                        <td
                          key={dayIdx}
                          draggable={isAdmin && getCell(emp.id, dayIdx).length > 0}
                          onDragStart={() => handleDragStart(emp.id, dayIdx)}
                          onDragOver={e => handleDragOver(e, emp.id, dayIdx)}
                          onDragLeave={() => setDragOver(null)}
                          onDrop={e => handleDrop(e, emp.id, dayIdx)}
                          onDragEnd={handleDragEnd}
                          style={{
                            ...TD, verticalAlign: 'top',
                            background: isDropTarget
                              ? 'rgba(96,165,250,.18)'
                              : isSrcCell ? 'rgba(245,158,11,.1)' : undefined,
                            outline: isDropTarget ? '2px dashed rgba(96,165,250,.6)' : undefined,
                            transition: 'background .1s, outline .1s',
                            cursor: isAdmin && getCell(emp.id, dayIdx).length > 0 ? 'grab' : undefined,
                          }}
                        >
                          {marca && <MarcaBadge marca={marca} />}
                          {isAdmin ? (
                            <CellEditor
                              values={getCell(emp.id, dayIdx)}
                              installations={installations}
                              descs={grid['__descs']?.[String(emp.id)]?.[String(dayIdx)] || {}}
                              onAdd={val => addToCell(emp.id, dayIdx, val)}
                              onRemove={idx => removeFromCell(emp.id, dayIdx, idx)}
                              onDescChange={(val, desc) => setItemDesc(emp.id, dayIdx, val, desc)}
                            />
                          ) : (
                            <div>
                              {getCell(emp.id, dayIdx).length > 0
                                ? getCell(emp.id, dayIdx).map((v, i) => (
                                    <div key={i} style={{ fontSize: 11, color: 'var(--text)', lineHeight: 1.5 }}>{v}</div>
                                  ))
                                : !marca && <span style={{ fontSize: 12, color: '#2a3050' }}>—</span>
                              }
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-dim)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>💡 Arrastra una celda con instalaciones a otra persona para copiar</span>
          {groups.length > 0 && <span>🔗 Las celdas de un grupo se sincronizan al editar (instalación y comentario)</span>}
        </div>
      )}

      {!isAdmin && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>
          Solo los administradores pueden editar la planificación
        </div>
      )}

      {dayDetail !== null && myEmployee && (
        <DayDetailModal
          dayIdx={dayDetail}
          date={weekDates[dayDetail]}
          myEmployee={myEmployee}
          employees={employees}
          grid={grid}
          marcas={marcas}
          myDescs={grid['__descs']?.[String(myEmployee.id)]?.[String(dayDetail)] || {}}
          onClose={() => setDayDetail(null)}
        />
      )}
    </div>
  );
}

function AddGroupForm({ employees, onAdd }) {
  const [selected, setSelected] = useState([]);

  function toggle(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }
  function handleAdd() {
    if (selected.length < 2) return;
    onAdd(selected);
    setSelected([]);
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {employees.map(e => {
          const id = String(e.id);
          const on = selected.includes(id);
          return (
            <button key={e.id} type="button" onClick={() => toggle(id)}
              style={{
                background: on ? 'rgba(245,158,11,.2)' : 'var(--surface2)',
                color: on ? 'var(--amber)' : 'var(--text-dim)',
                border: on ? '1px solid rgba(245,158,11,.5)' : '1px solid var(--border)',
                borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700,
              }}>
              {on ? '✓ ' : ''}{e.nombre}
            </button>
          );
        })}
      </div>
      <button
        onClick={handleAdd}
        disabled={selected.length < 2}
        style={{ background: 'rgba(245,158,11,.15)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,.4)',
          padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12,
          opacity: selected.length < 2 ? 0.45 : 1 }}>
        + Crear grupo{selected.length > 0 ? ` (${selected.length})` : ''}
      </button>
    </div>
  );
}

function MarcaBadge({ marca, style: extraStyle }) {
  const cfg = {
    FESTIVO:    { bg: 'rgba(251,191,36,.15)', color: '#fbbf24', label: '🗓 FESTIVO' },
    BAJA:       { bg: 'rgba(248,113,113,.15)', color: '#f87171', label: '🏥 BAJA' },
    VACACIONES: { bg: 'rgba(96,165,250,.15)', color: '#60a5fa', label: '🏖 VACACIONES' },
  }[marca] || { bg: 'rgba(96,165,250,.15)', color: '#60a5fa', label: '🏖 VACACIONES' };
  return (
    <div style={{
      display: 'inline-block', fontSize: 9, fontWeight: 900, padding: '2px 6px',
      borderRadius: 4, marginBottom: 4, letterSpacing: '.05em',
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}44`,
      ...extraStyle,
    }}>
      {cfg.label}
    </div>
  );
}

function CellEditor({ values, installations, descs = {}, onAdd, onRemove, onDescChange }) {
  const [inp, setInp] = useState('');
  const [editingDesc, setEditingDesc] = useState(null);

  function commit(val) {
    const v = (val || inp).trim();
    if (v) onAdd(v);
    setInp('');
  }

  return (
    <div style={{ minWidth: 90 }}>
      {values.map((v, i) => (
        <div key={i}>
          <div className="plan-chip">
            <span className="plan-chip-label" title={v}>{v}</span>
            <button
              onClick={() => setEditingDesc(editingDesc === v ? null : v)}
              title="Descripción del trabajo"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
                padding: '0 2px', lineHeight: 1, flexShrink: 0,
                color: descs[v] ? '#f59e0b' : '#4e5a7a' }}>
              ✎
            </button>
            <button onClick={() => { onRemove(i); if (editingDesc === v) setEditingDesc(null); }}
              style={{ background: 'none', border: 'none', color: '#f87171',
                cursor: 'pointer', fontSize: 13, padding: '0 1px', lineHeight: 1, flexShrink: 0 }}>
              ×
            </button>
          </div>
          {editingDesc === v && (
            <textarea
              autoFocus
              value={descs[v] || ''}
              onChange={e => onDescChange && onDescChange(v, e.target.value)}
              placeholder="Descripción del trabajo…"
              rows={3}
              style={{
                display: 'block', width: '100%', resize: 'vertical', boxSizing: 'border-box',
                background: '#070910', border: '1px solid #f59e0b', borderTop: 'none',
                borderRadius: '0 0 4px 4px', color: '#d8e2f4', fontSize: 10,
                padding: '4px 6px', fontFamily: 'inherit', lineHeight: 1.5, marginTop: 0,
              }}
            />
          )}
        </div>
      ))}
      <input
        type="text"
        list="inst-list"
        value={inp}
        onChange={e => {
          const val = e.target.value;
          setInp(val);
          if (installations.some(inst => instNombre(inst) === val)) {
            onAdd(val);
            setInp('');
          }
        }}
        onBlur={() => { if (inp.trim()) commit(); }}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        placeholder={values.length ? '+' : '—'}
        style={CELL_INP}
      />
    </div>
  );
}

const TH     = { background: 'var(--surface)', border: '1px solid var(--border)', padding: '10px 12px',
  fontSize: 12, fontWeight: 800, color: 'var(--text)', textAlign: 'center' };
const TH_EMP = { ...TH, textAlign: 'left', minWidth: 140, width: 160 };
const TD     = { border: '1px solid var(--border)', padding: '5px 8px', textAlign: 'center',
  verticalAlign: 'middle' };
const TD_EMP = { ...TD, textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--text)',
  padding: '8px 12px', whiteSpace: 'nowrap' };
const BTN_NAV  = { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)',
  padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 16, fontWeight: 700 };
const CELL_INP = { background: 'transparent', border: 'none', color: '#8899b8', fontSize: 11,
  width: '100%', padding: '2px 4px', fontFamily: 'inherit', textAlign: 'center', outline: 'none' };

function DayDetailModal({ dayIdx, date, myEmployee, employees, grid, marcas, myDescs = {}, onClose }) {
  const myVals = toArr(grid[String(myEmployee.id)]?.[String(dayIdx)]);
  const ds = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  let marca = null;
  if (marcas.festivos.includes(ds)) marca = 'FESTIVO';
  else if (marcas.bajas[String(myEmployee.id)]?.includes(ds)) marca = 'BAJA';
  else if (marcas.vacaciones[String(myEmployee.id)]?.includes(ds)) marca = 'VACACIONES';

  const companions = employees.filter(e => {
    if (e.id === myEmployee.id) return false;
    return toArr(grid[String(e.id)]?.[String(dayIdx)]).some(v => myVals.includes(v));
  });

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#10131e', border: '1px solid #1c2035', borderRadius: 14,
        padding: 22, maxWidth: 420, width: '100%',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--amber)' }}>
              {DIAS[dayIdx]} {date.getDate()}/{date.getMonth()+1}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{myEmployee.nombre}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ marginBottom: companions.length > 0 ? 14 : 0 }}>
          {marca && <MarcaBadge marca={marca} style={{ marginBottom: 8 }} />}
          {myVals.length > 0 ? myVals.map((v, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>{v}</div>
              {myDescs[v] && (
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4, lineHeight: 1.5 }}>
                  {myDescs[v]}
                </div>
              )}
            </div>
          )) : !marca && (
            <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '4px 0' }}>Sin trabajo asignado</div>
          )}
        </div>

        {companions.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#60a5fa', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '.06em' }}>
              Compañeros
            </div>
            {companions.map(c => (
              <div key={c.id} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#60a5fa' }}>·</span>{c.nombre}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
